import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCountry } from '@/contexts/CountryContext';

interface HeroSlide {
  src: string;
  alt: string;
  title: string;
  subtitle: string;
  /** Region tag shown above the title (e.g. "Northern Romania"). */
  region?: string;
}

const albaniaSlides: HeroSlide[] = [
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1775241419265_384d50a9.jpg',
    alt: 'Turquoise waters and white sand of the Albanian Riviera, luxury Albania travel destination with Mediterranean coastline',
    title: 'Discover\nAlbania',
    subtitle: "Europe's last secret — where ancient heritage meets pristine Mediterranean shores",
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1776973064723_882c03d8.jpg',
    alt: "Aerial view of the Albanian Riviera near Ksamil and Saranda with turquoise Ionian Sea, limestone cliffs, and hidden coves on Albania's Mediterranean coast",
    title: 'The Albanian\nRiviera Awaits',
    subtitle: "Crystal waters, hidden coves, and untouched coastlines that rival the world's finest",
  },
  {
    src: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=2000&q=80',
    alt: 'Snow-capped peaks and emerald valleys of the Albanian Alps (Accursed Mountains) with Theth and Valbona hiking',
    title: 'Into the\nAccursed Mountains',
    subtitle: 'Dramatic alpine peaks, emerald valleys, and legendary Albanian hospitality',
  },
];

// One slide for each region of the country — North, Central and South —
// plus the intro and the iconic Transfăgărășan drive.
const romaniaSlides: HeroSlide[] = [
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782406694217_33fd396b.png',
    alt: 'Bran Castle in Transylvania, Romania, the Gothic medieval fortress famously associated with Dracula, set against the Carpathian Mountains',
    title: 'Discover\nRomania',
    subtitle: 'Land of the Carpathians — where Gothic castles, medieval citadels, and wild forests await',
    region: 'Romania',
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782406795890_6a2ab537.png',
    alt: 'Voroneț Monastery in Bucovina, northern Romania, a UNESCO painted monastery covered in vivid blue Byzantine frescoes',
    title: 'The North\nBucovina & Maramureș',
    subtitle: 'Painted monasteries glowing in Voroneț blue, carved wooden gates and villages where traditions never faded',
    region: 'Northern Romania',
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782406711915_8dbdbfed.jpg',
    alt: 'Peleș Castle in Sinaia, Romania, a Neo-Renaissance royal palace with ornate turrets set in the Carpathian forests of the Prahova Valley',
    title: 'The Centre\nTransylvania & Brașov',
    subtitle: 'Royal palaces, Saxon citadels and Carpathian estates in the fairytale heart of the country',
    region: 'Central Romania',
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1782406729379_e48b2fcd.jpg',
    alt: 'The winding Transfăgărășan mountain road snaking through the Făgăraș Mountains of the Romanian Carpathians under dramatic alpine peaks',
    title: 'The\nTransfăgărășan',
    subtitle: "One of the world's most spectacular alpine drives, climbing past glacial lakes to 2,000 metres",
    region: 'Central Romania',
  },
  {
    src: 'https://d64gsuwffb70l.cloudfront.net/69c20284ac08f4efe2afa592_1782757845412_836211b0.jpeg',
    alt: "Pelicans gliding across the reed-lined channels of the Danube Delta in southern Romania, a UNESCO Biosphere Reserve and Europe's largest wetland",
    title: 'The South\nBucharest & the Delta',
    subtitle: "Belle Époque boulevards, the buzzing capital and the wild waterways of Europe's largest wetland",
    region: 'Southern Romania',
  },
];

const HeroSection: React.FC = () => {
  const { country, label } = useCountry();
  const navigate = useNavigate();
  const slides = country === 'romania' ? romaniaSlides : albaniaSlides;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Reset to first slide when the country (and therefore slide set) changes.
  useEffect(() => {
    setCurrentSlide(0);
  }, [country]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setIsTransitioning(false);
      }, 800);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    if (index === currentSlide) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsTransitioning(false);
    }, 800);
  };

  const scrollToDestinations = () => {
    const el = document.getElementById('destinations');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Straight to the booking form.
  const goToBooking = () => {
    navigate(country === 'romania' ? '/romania/booking' : '/booking');
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Images */}
      {slides.map((img, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={img.src}
            alt={img.alt}
            className="w-full h-full object-cover"
            loading={index === 0 ? 'eager' : 'lazy'}
            decoding={index === 0 ? 'sync' : 'async'}
            fetchPriority={index === 0 ? 'high' : 'low'}
          />
        </div>
      ))}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent" />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6">
        <div className={`transition-all duration-700 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
          {/* Decorative line + region tag */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-12 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
              Zera Travel / {slides[currentSlide].region ?? label}
            </span>
            <div className="w-12 h-[1px] bg-[#C9A961]" />
          </div>

          {/* Title */}
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light text-white leading-[1.1] whitespace-pre-line mb-6">
            {slides[currentSlide].title}
          </h1>

          {/* Subtitle */}
          <p className="font-sans text-sm md:text-base font-light text-white/80 max-w-xl mx-auto tracking-wide leading-relaxed mb-10">
            {slides[currentSlide].subtitle}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={goToBooking}
              className="book-cta font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-all duration-300"
            >
              Book Now
            </button>
            <button
              onClick={scrollToDestinations}
              className="font-sans text-[11px] tracking-[0.25em] uppercase px-10 py-4 border border-white/50 text-white hover:bg-white/10 transition-all duration-300"
            >
              Explore {label}
            </button>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex items-center gap-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-500 ${
                index === currentSlide
                  ? 'w-10 h-[2px] bg-[#C9A961]'
                  : 'w-6 h-[2px] bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 right-8 hidden lg:flex flex-col items-center gap-2">
          <span className="font-sans text-[9px] tracking-[0.2em] uppercase text-white/50 [writing-mode:vertical-lr]">
            Scroll
          </span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-white/50 to-transparent animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
